/**
 * Filter builder for a widget.
 *
 * Produces `FilterClause[]`; the expression itself is built by
 * `buildWhereClause`, which is field-type aware (numeric columns are compared
 * unquoted) and shared by both service adapters.
 *
 * The generated expression is shown read-only so an admin can see exactly what
 * will be sent to the service — useful when a filter returns nothing.
 */

import { useState } from 'react';
import { Plus, X } from 'lucide-react';
import { createId } from '../../services/storage';
import { getDataSourceAdapter } from '../services/dashboardDataService';
import { buildWhereClause } from '../utils/dashboardUtils';
import {
  OPERATOR_LABELS,
  VALUELESS_OPERATORS,
  type DashboardDataSource,
  type DashboardField,
  type FilterClause,
  type FilterOperator,
} from '../types/dashboardTypes';

interface Props {
  filters: FilterClause[];
  fields: DashboardField[];
  source?: DashboardDataSource;
  onChange: (filters: FilterClause[]) => void;
}

export default function FilterEditor({ filters, fields, source, onChange }: Props) {
  // Distinct values are fetched lazily, per clause, only when asked for.
  const [suggestions, setSuggestions] = useState<Record<string, string[]>>({});
  const [loadingFor, setLoadingFor] = useState<string | null>(null);

  const update = (id: string, changes: Partial<FilterClause>) =>
    onChange(filters.map((clause) => (clause.id === id ? { ...clause, ...changes } : clause)));

  const add = () =>
    onChange([
      ...filters,
      { id: createId('flt'), field: '', operator: '=', value: '', conjunction: 'AND' },
    ]);

  const remove = (id: string) => onChange(filters.filter((clause) => clause.id !== id));

  const loadSuggestions = async (clause: FilterClause) => {
    if (!source || !clause.field || suggestions[clause.id]) return;
    setLoadingFor(clause.id);
    try {
      const values = await getDataSourceAdapter(source).fetchDistinctValues(source, clause.field);
      setSuggestions((current) => ({ ...current, [clause.id]: values }));
    } finally {
      setLoadingFor(null);
    }
  };

  const expression = buildWhereClause(filters, fields);

  return (
    <div className="space-y-2">
      <div className="flex items-center justify-between">
        <span className="text-xs font-semibold text-slate-600">Filter</span>
        <button
          type="button"
          onClick={add}
          className="inline-flex items-center gap-1 text-[11px] font-semibold text-blue-600 hover:text-blue-800"
        >
          <Plus className="h-3 w-3" />
          Add condition
        </button>
      </div>

      {filters.length === 0 ? (
        <p className="rounded-lg bg-slate-50 px-3 py-2 text-[11px] text-slate-400">
          No filter — the widget uses every record in the layer.
        </p>
      ) : (
        <div className="space-y-2">
          {filters.map((clause, index) => {
            const needsValue = !VALUELESS_OPERATORS.includes(clause.operator);
            const listId = `suggestions-${clause.id}`;

            return (
              <div key={clause.id} className="rounded-lg border border-slate-200 bg-slate-50 p-2">
                <div className="flex flex-wrap items-center gap-1.5">
                  {index === 0 ? (
                    <span className="w-12 flex-shrink-0 text-[11px] font-semibold text-slate-500">
                      Where
                    </span>
                  ) : (
                    <select
                      value={clause.conjunction}
                      onChange={(event) =>
                        update(clause.id, { conjunction: event.target.value as 'AND' | 'OR' })
                      }
                      aria-label="Join with previous condition"
                      className="w-12 flex-shrink-0 rounded border border-slate-200 bg-white px-1 py-1 text-[11px] text-slate-700 outline-none"
                    >
                      <option value="AND">And</option>
                      <option value="OR">Or</option>
                    </select>
                  )}

                  <select
                    value={clause.field}
                    onChange={(event) => {
                      update(clause.id, { field: event.target.value });
                      setSuggestions((current) => {
                        const next = { ...current };
                        delete next[clause.id];
                        return next;
                      });
                    }}
                    aria-label="Filter field"
                    className="min-w-0 flex-1 rounded border border-slate-200 bg-white px-1.5 py-1 text-[11px] text-slate-700 outline-none"
                  >
                    <option value="">Field…</option>
                    {fields.map((field) => (
                      <option key={field.name} value={field.name}>
                        {field.alias}
                      </option>
                    ))}
                  </select>

                  <select
                    value={clause.operator}
                    onChange={(event) =>
                      update(clause.id, { operator: event.target.value as FilterOperator })
                    }
                    aria-label="Filter operator"
                    className="min-w-0 flex-1 rounded border border-slate-200 bg-white px-1.5 py-1 text-[11px] text-slate-700 outline-none"
                  >
                    {(Object.keys(OPERATOR_LABELS) as FilterOperator[]).map((operator) => (
                      <option key={operator} value={operator}>
                        {OPERATOR_LABELS[operator]}
                      </option>
                    ))}
                  </select>

                  {needsValue && (
                    <>
                      <input
                        value={clause.value}
                        onChange={(event) => update(clause.id, { value: event.target.value })}
                        onFocus={() => void loadSuggestions(clause)}
                        list={listId}
                        placeholder={loadingFor === clause.id ? 'Loading values…' : 'Value'}
                        aria-label="Filter value"
                        className="min-w-0 flex-1 rounded border border-slate-200 bg-white px-1.5 py-1 text-[11px] text-slate-700 outline-none focus:border-blue-300"
                      />
                      <datalist id={listId}>
                        {(suggestions[clause.id] ?? []).map((value) => (
                          <option key={value} value={value} />
                        ))}
                      </datalist>
                    </>
                  )}

                  <button
                    type="button"
                    onClick={() => remove(clause.id)}
                    aria-label="Remove condition"
                    className="flex-shrink-0 rounded p-1 text-slate-400 transition hover:bg-red-50 hover:text-red-600"
                  >
                    <X className="h-3.5 w-3.5" />
                  </button>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {expression && (
        <div className="rounded-lg bg-slate-900 px-2.5 py-1.5">
          <p className="text-[10px] font-medium uppercase tracking-wide text-slate-400">
            Sent to the service
          </p>
          <code className="block break-words text-[11px] text-teal-300">{expression}</code>
        </div>
      )}
    </div>
  );
}
